// Contracts — vista CAPTURE ingest. This is the surface the recorder talks to
// while it is filming, so every shape here is written for interruption.
//
// The flow (mirrors kids' pack multipart, plus resume, which kids does not need):
//
//   POST /api/vista/capture/open    { taskSlug, orientation, … }
//        → { captureId, key, uploadId, nextPart }      row written AT OPEN
//   PUT  /api/vista/capture/part    raw bytes + headers
//        X-Capture-Id · X-Mpu-Part (1-based)
//        → { partNumber, etag }                        (binary — no zod body)
//   POST /api/vista/capture/resume  { captureId }
//        → { key, uploadId, nextPart, partsUploaded, bytesReceived }
//   POST /api/vista/capture/finish  { captureId, durationSeconds, … }
//        → { ok, status }
//   POST /api/vista/capture/abort   { captureId, reason } → { ok }
//
// Why resume exists at all: a twenty-minute take will meet a dropped network,
// and R2 keeps an unfinished multipart upload for 7 days. The worker is
// stateless, so uploadId and every part's ETag live in D1 (`uploads`) and the
// client re-attaches instead of re-filming.
//
// The capture ROW is written when the upload OPENS, not when it completes:
// otherwise a clip that died mid-upload is invisible to everyone, including the
// person who filmed it.
import { z } from 'zod';
import { VistaCaptureSourceSchema, VistaCaptureStatusSchema, VistaOrientationSchema } from './enums';

// Part size is fixed by R2's own floor (5 MiB minimum for every part but the
// last) and by how much re-upload a flaky mobile link should cost on retry.
// 8 MiB ≈ 12 s of 1080p30 at ~5.5 Mbit/s.
export const VISTA_PART_BYTES = 8 * 1024 * 1024;

// Длинная съёмка режется на блоки. Это не техническое ограничение, а тактика
// выживания: у референсного продукта люди теряют запись целиком, когда экран
// засыпает, — у нас потеря стоит одного блока, а не всей сессии.
//
// 20 минут (Tim, 12.08, было 15): совпадает с верхней границей клипа у Lightly
// («1–20 минут, ориентир гибкий»), то есть каждый блок сам по себе — готовый
// сдаваемый клип, а не полуфабрикат, который надо склеивать.
export const VISTA_SEGMENT_SECONDS = 20 * 60;

// The client's own spec is 1–20 minutes PER CLIP, and they call it a "flexible
// guideline" — not a hard wall. Our session is not one clip: it is cut into
// VISTA_SEGMENT_SECONDS (15 min) blocks, and each block on its own sits well
// inside the client's window. So the ceiling here is not "20 minutes" — it is
// a safety cap on the whole SESSION against battery/storage running out, set
// generously above a single segment.
export const VISTA_MIN_CLIP_SECONDS = 60;
export const VISTA_MAX_CLIP_SECONDS = 60 * 60;

/**
 * Below this ratio of encoded-video-duration to wall-clock, the take is a
 * re-shoot rather than a delivery. It is the gate that catches "the timer said
 * 20 minutes and the video froze at 4" — the failure mode that made the whole
 * integrity layer necessary in the first place.
 */
export const VISTA_INTEGRITY_MIN_RATIO = 0.9;

export const VistaCaptureOpenReqSchema = z.object({
  taskSlug: z.string().trim().min(1).max(80),
  orientation: VistaOrientationSchema,
  // По умолчанию — рекордер. 'upload' говорит серверу, что гейта целостности
  // здесь не будет: у готового файла нет счётчика кадров, с которым его можно
  // сравнить.
  source: VistaCaptureSourceSchema.optional(),
  staticCamera: z.boolean().optional(),
  // Client clock at the moment filming started. Advisory: the worker keeps its
  // own timestamps, but the daily rollup buckets by capture time, and only the
  // device knows when the take actually began.
  recordedAt: z.number().int().optional(),
  fileName: z.string().trim().max(200).optional(),
  contentType: z.string().trim().max(100).optional(),
  // Which block of a segmented take this is (0-based), and the id of the first
  // block when it is a continuation.
  segmentIndex: z.number().int().min(0).max(200).optional(),
  segmentOf: z.string().trim().max(64).optional(),
  // Compact device fingerprint from the recorder's own userAgent (F10): clips
  // from different phones are otherwise indistinguishable to staff. Built
  // client-side, never trusted beyond a display string.
  // 200, а не 120: с 12.08 сюда же кладётся, какой объектив выбран и завёлся
  // ли звук — без этого разбор «звука нет» и «картинка прыгает» превращается
  // в переписку вместо запроса к базе.
  device: z.string().trim().max(200).optional(),
});
export type VistaCaptureOpenReq = z.infer<typeof VistaCaptureOpenReqSchema>;

export const VistaCaptureOpenResSchema = z.object({
  captureId: z.string(),
  key: z.string(),
  // R2 multipart upload id. Real ones run ~343 characters — do NOT tighten this
  // to 256: that exact guess broke every chunked upload on kids (2026-08-03).
  uploadId: z.string().min(1).max(1400),
  nextPart: z.number().int().positive(),
});
export type VistaCaptureOpenRes = z.infer<typeof VistaCaptureOpenResSchema>;

/** One uploaded part's receipt (partNumber is 1-based, etag from R2.uploadPart). */
export const VistaMpuPartSchema = z.object({
  partNumber: z.number().int().positive(),
  etag: z.string().min(1),
});
export type VistaMpuPart = z.infer<typeof VistaMpuPartSchema>;

// POST /capture/resume — hand back everything needed to continue an interrupted
// upload. The client sends only the capture id: key, uploadId and the next part
// number are server state, and letting the client assert them would let a stale
// tab overwrite a newer attempt.
export const VistaCaptureResumeReqSchema = z.object({
  captureId: z.string().trim().min(1).max(64),
});
export type VistaCaptureResumeReq = z.infer<typeof VistaCaptureResumeReqSchema>;

export const VistaCaptureResumeResSchema = z.object({
  key: z.string(),
  uploadId: z.string().min(1).max(1400),
  nextPart: z.number().int().positive(),
  partsUploaded: z.number().int().min(0),
  bytesReceived: z.number().int().min(0),
  // R2 discards an unfinished upload after 7 days; past that the only honest
  // answer is "film it again", and the UI must say so rather than retry.
  expired: z.boolean(),
});
export type VistaCaptureResumeRes = z.infer<typeof VistaCaptureResumeResSchema>;

// POST /capture/finish — close the upload and record what was actually encoded.
// The numbers come from the recorder, not from the container: the point of the
// integrity gate is to compare what the encoder produced against the clock.
export const VistaCaptureFinishReqSchema = z.object({
  captureId: z.string().trim().min(1).max(64),
  durationSeconds: z.number().int().min(0).max(4 * 60 * 60),
  width: z.number().int().min(0).max(8192),
  height: z.number().int().min(0).max(8192),
  fps: z.number().min(0).max(240),
  // Encoded frames actually muxed, and wall-clock of the take. The worker
  // recomputes the ratio from these rather than trusting a number the client
  // could round into passing.
  frameCount: z.number().int().min(0).optional(),
  wallClockSeconds: z.number().min(0).max(4 * 60 * 60).optional(),
  sha256: z.string().trim().length(64).optional(),
  // Имя исходного файла при загрузке готового видео — единственный след того,
  // чем и когда человек снимал; для рекордера оно наше и бессмысленно.
  fileName: z.string().trim().max(200).optional(),
  gpsPresent: z.boolean().optional(),
  country: z.string().trim().max(2).optional(),
  // Coarse only. Precise coordinates are never sent and never stored — the
  // footage already records far more of a bystander's life than a photo does.
  city: z.string().trim().max(80).optional(),
});
export type VistaCaptureFinishReq = z.infer<typeof VistaCaptureFinishReqSchema>;

export const VistaCaptureFinishResSchema = z.object({
  ok: z.literal(true),
  status: VistaCaptureStatusSchema,
  durationSeconds: z.number().int(),
  integrityRatio: z.number().nullable(),
});
export type VistaCaptureFinishRes = z.infer<typeof VistaCaptureFinishResSchema>;

// Диагностика записи в момент провала. Это ЕДИНСТВЕННЫЕ числа, по которым
// можно установить причину брака (медленная камера? канал? заморозка?) —
// клип da33f622 (11.08, 15 минут в мусор) разбирали вслепую именно потому,
// что движок посчитал их, показал общую фразу и выбросил.
export const VistaCaptureDiagSchema = z.object({
  encodedFrames: z.number().int().min(0),
  droppedFrames: z.number().int().min(0),
  wallClockSeconds: z.number().min(0).max(4 * 60 * 60),
  realVideoSeconds: z.number().min(0).max(4 * 60 * 60),
  clientError: z.string().max(200).optional(),
  // Сеть в момент падения. Заливка идёт всю съёмку, и её отставание —
  // самостоятельная причина брака: канал не тянет → хвост копится → клип
  // умирает не от камеры. Без этих двух чисел разбор упирался в догадки.
  uploadBytesPerSec: z.number().min(0).optional(),
  uploadBufferedBytes: z.number().min(0).optional(),
});
export type VistaCaptureDiag = z.infer<typeof VistaCaptureDiagSchema>;

// POST /capture/abort — the recorder failed its own gate, or the person walked
// away.
//
// Что происходит с уже залитыми частями — зависит от причины:
//   • user_cancelled / expired — части выбрасываются: человек сам отказался,
//     либо R2 уже всё удалил.
//   • integrity / error — если частей достаточно, чтобы в них был материал,
//     сервер ЗАВЕРШАЕТ multipart тем, что доехало, и кладёт клип в карантин.
//     Пятнадцать минут съёмки — это улика о том, что сломалось, и возможно
//     годный материал; уничтожать их из-за счётчика кадров нельзя (урок
//     da33f622: 402 МБ стёрты, смотреть не на что).
export const VistaCaptureAbortReqSchema = z.object({
  captureId: z.string().trim().min(1).max(64),
  reason: z.enum(['integrity', 'user_cancelled', 'error', 'expired']),
  stats: VistaCaptureDiagSchema.optional(),
});
export type VistaCaptureAbortReq = z.infer<typeof VistaCaptureAbortReqSchema>;

export const VistaCaptureAbortResSchema = z.object({
  ok: z.literal(true),
  // true — части не выброшены: клип запечатан обрезанным и лежит в карантине.
  salvaged: z.boolean(),
});
export type VistaCaptureAbortRes = z.infer<typeof VistaCaptureAbortResSchema>;
